import type { Address, AddressTypeValue, BaseStatusValue, CreateAddressRequest } from '@my/directory-api';
import { COUNTRIES } from '@my/platform/options';
import type { ServiceTag } from '@my/tms-api';
import type { SelectInputOption } from '@my/ui';

/*
 * The Locations editor's draft and rules, shared by the Locations screen and
 * the "New location" modal on the task forms. One copy, so a location made
 * from a task is exactly the object the Locations screen would have made:
 * same fields, same validation, same service tags, same request.
 */

/**
 * Country options — `${flag} ${name}` matches the rest of the suite. The
 * grid renders bare ISO codes (Windows flag-emoji rendering quirk) but
 * the editor's dropdown uses the richer label so the picker reads
 * consistently with the parties / organisation forms.
 */
export const COUNTRY_OPTIONS: SelectInputOption[] = COUNTRIES.map((c) => ({
  value: c.isoCode,
  label: `${c.flagEmoji} ${c.name}`,
}));

/**
 * Local draft shape for the inline editor — same fields the
 * `<AddressInlineEditor>` reads / writes, plus `status` (operators
 * can suspend a stop without deleting it). `addressType` is fixed
 * to `Location` for new rows from this page (standalone facilities;
 * party-linked Task rows are created from the Party Details page,
 * not here) and echoed back unchanged on edit.
 */
export interface AddressDraft {
  code: string;
  name: string;
  fullAddress: string;
  city: string;
  postcode: string;
  countryIsoCode: string;
  latitude: number | null;
  longitude: number | null;
  placeId: string;
  /**
   * Where the coordinates came from: `Google` (picked / Find on map) or
   * `Manual` (pin dragged or tapped). Forwarded on save so Directory never
   * refreshes a hand-placed pin back off the gate.
   */
  coordinatesSource: 'Google' | 'Manual' | null;
  status: BaseStatusValue;
  /**
   * Comma-joined service-tag names. Same wire shape as `Address.serviceTags`
   * — the BE canonicalises (trim each entry, dedupe case-insensitively)
   * on every save so the wire shape is always tidy.
   */
  serviceTags: string;
}

export const EMPTY_ADDRESS_DRAFT: AddressDraft = {
  code: '',
  name: '',
  fullAddress: '',
  city: '',
  postcode: '',
  countryIsoCode: 'IE',
  latitude: null,
  longitude: null,
  placeId: '',
  coordinatesSource: null,
  status: 'Active',
  serviceTags: '',
};

export function addressToDraft(a: Address): AddressDraft {
  return {
    code: a.code ?? '',
    name: a.name,
    fullAddress: a.fullAddress,
    city: a.city ?? '',
    postcode: a.postcode ?? '',
    countryIsoCode: a.countryIsoCode,
    latitude: a.latitude ?? null,
    longitude: a.longitude ?? null,
    placeId: a.placeId,
    coordinatesSource: a.coordinatesSource ?? null,
    status: a.status,
    serviceTags: a.serviceTags ?? '',
  };
}

/**
 * Valid for save when name + address + 2-letter country are present AND
 * lat/lng are non-null (geolocation verified). Same rule as every other
 * AddressInlineEditor in the suite.
 */
export function isAddressDraftValid(draft: AddressDraft): boolean {
  return (
    draft.name.trim().length > 0 &&
    draft.fullAddress.trim().length > 0 &&
    draft.placeId.trim().length > 0 &&
    draft.countryIsoCode.length === 2 &&
    draft.latitude != null &&
    draft.longitude != null
  );
}

/**
 * Service tags applicable to the Locations editor — only Active tags whose
 * `applyToServiceTypes` includes `Locations` (or is null/empty, i.e. "applies
 * everywhere"). Sorted alphabetically so the chip row reads predictably.
 */
export function applicableLocationTags(tags: ServiceTag[] | undefined): ServiceTag[] {
  return (tags ?? [])
    .filter((tag) => {
      if (tag.status !== 'Active') return false;
      const apply = tag.applyToServiceTypes?.trim();
      if (!apply) return true;
      return apply
        .split(',')
        .map((s) => s.trim())
        .includes('Locations');
    })
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

/**
 * Lower-cased set of the draft's active tag names — drives the chip row's
 * on/off state. Same canonicalisation the BE applies on save (case-insensitive).
 */
export function draftTagSet(serviceTags: string): Set<string> {
  return new Set(
    serviceTags
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => s.toLowerCase()),
  );
}

/** The draft with one tag switched on or off. */
export function toggleDraftTag(draft: AddressDraft, tag: ServiceTag): AddressDraft {
  const lower = tag.name.toLowerCase();
  const list = draft.serviceTags
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const idx = list.findIndex((s) => s.toLowerCase() === lower);
  if (idx >= 0) list.splice(idx, 1);
  else list.push(tag.name);
  return { ...draft, serviceTags: list.join(',') };
}

/** The create request for a new standalone location. */
export function buildCreateAddressRequest(
  organisationId: string,
  draft: AddressDraft,
): CreateAddressRequest {
  return {
    organisationId,
    // New rows from the Locations page are standalone facilities
    // — port, terminal, weighbridge — which is exactly what
    // `Location` represents. Party-specific Task addresses are
    // created from the Party Details page (which stamps partyId).
    addressType: 'Task' as AddressTypeValue,
    partyId: null,
    siteId: null,
    code: draft.code.trim() || null,
    name: draft.name.trim(),
    fullAddress: draft.fullAddress.trim(),
    city: draft.city.trim() || null,
    postcode: draft.postcode.trim() || null,
    countryIsoCode: draft.countryIsoCode.toUpperCase(),
    latitude: draft.latitude,
    longitude: draft.longitude,
    coordinatesSource: draft.coordinatesSource ?? null,
    placeId: draft.placeId.trim(),
    status: draft.status,
    // BE canonicalises (trim, dedupe case-insensitively) on save —
    // we send whatever the chip toggles produced. Empty string
    // serialises through fine; the BE treats blank == null.
    serviceTags: draft.serviceTags || null,
  };
}
